import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFetchEntities } from 'common/hooks/usePalmFetchEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { fetchTenants } from 'common/services/api/Tenants/Tenants.thunks';
import {
    ActionDefinition,
    ColumnDefinition,
    ColumnType,
    Operations } from 'common/types/TypeDefs';

import { TenantsDeleteDialogContent } from './TenantDeleteDialogContent';
import { TenantsCreateContent } from './TenantsEditDrawerContent';
import {
    addNewFilter,
    clearFilters,
    removeFilter,
    selectFilters } from './tenantsSlice';

export function TenantsTab() {
    const typeName = 'Tenant';
    const [processing, setProcessing] = useState<boolean>(false);
    const [sortModel, setSortModel] = usePalmSortModel();

    const shouldDisableCreateEditButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager ]);
    const shouldDisableOpenButton = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager, Roles.MultiTenantRO, Roles.UserManager ]);
    const shouldDisableDeleteButton = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters
    );

    const params = useParams();
    const suiteId = params.suiteId;
    const fetchEntities = usePalmFetchEntities(
        typeName,
        { id: suiteId ?? '' },
        fetchTenants,
        setProcessing
    );

    const onGetRowLink = useCallback((tenantId: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.Users}`;
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: shouldDisableOpenButton, urlToOpen: onGetRowLink },
        { action: Operations.Edit, disabled: shouldDisableCreateEditButtons },
        { action: Operations.Delete, disabled: shouldDisableDeleteButton }
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'id', columnName: 'Tenant ID', columnType: ColumnType.First, shouldFilterByExactMatch: true },
        { columnId: 'name', columnName: 'Tenant Name', columnType: ColumnType.Tooltip },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action }
    ];

    return <PalmDataGrid
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        contentFunction={TenantsCreateContent}
        deleteFunction={TenantsDeleteDialogContent}
        title={typeName}
        getRowLink={shouldDisableOpenButton ? undefined : onGetRowLink}
        isCreateButtonDisabled={shouldDisableCreateEditButtons}
        filter={filterProp}
        processing={processing}
        refreshDataGridClick={() => fetchEntities()}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
    />;
}
